import { forwardRef } from "react";
import { twMerge } from "tailwind-merge";

import { BaseButton, type BaseButtonProps } from "./BaseButton";
import { type ButtonKind } from "./ButtonKinds";
import { IconSizeType } from "./LegacyButton";

import type { PolymorphicRef } from "types";

const KIND_CLASSES: Record<ButtonKind, string> = {
  blue:
    "text-white bg-blue-shade-20 hover:bg-blue hover:text-white active:bg-blue-shade-40 " +
    "disabled:bg-black-tint-90 disabled:text-black-tint-55",

  green:
    "text-green-shade-20 bg-green-tint-90 hover:text-green " +
    "disabled:bg-black-tint-90 disabled:text-black-tint-55",

  greenPrimary:
    "bg-green-shade-20 text-white hover:bg-green-shade-40 active:bg-green-tint-20 " +
    "disabled:bg-black-tint-90 disabled:text-black-tint-55",

  gray:
    "border border-black-tint-97 bg-black-tint-97 text-black-tint-20 hover:text-blue-shade-40 " +
    "disabled:bg-black-tint-93 disabled:text-black-tint-70 data-[selected]:border-blue-tint-40 " +
    "data-[selected]:bg-blue-tint-93 data-[selected]:text-blue-tint-40 group-data-[dark]/themable:border-black-tint-20 " +
    "group-data-[dark]/themable:bg-black-tint-20 group-data-[dark]/themable:text-white " +
    "hover:group-data-[dark]/themable:bg-black-tint-30 disabled:group-data-[dark]/themable:bg-black-tint-20 " +
    "disabled:group-data-[dark]/themable:text-black-tint-40 hover:disabled:group-data-[dark]/themable:bg-black-tint-20 " +
    "group-data-[dark]/themable:data-[selected]:border-blue-shade-20 " +
    "group-data-[dark]/themable:data-[selected]:bg-blue-shade-40",

  white:
    "border border-black-tint-90 bg-white text-black-tint-40 hover:bg-black-tint-90 " +
    "hover:text-black-tint-20 active:bg-black-tint-97 disabled:bg-black-tint-93 disabled:text-black-tint-70 " +
    "disabled:cursor-not-allowed",

  // Used for destructive actions
  red:
    "border border-red-tint-90 bg-white text-red hover:bg-red-tint-95 hover:text-red-shade-20 " +
    "active:bg-red-tint-97 active:text-red-shade-20 disabled:bg-black-tint-90 disabled:text-black-tint-55",

  headerNav:
    "bg-white text-black-tint-20 active:bg-black-tint-97 " +
    "hover:bg-black-tint-90 hover:text-black-tint-20 " +
    "disabled:bg-white disabled:text-black-tint-40 disabled:cursor-not-allowed " +
    "disabled:hover:bg-white disabled:hover:text-black-tint-40 " +
    "group-data-[dark]/themable:bg-black-tint-15 group-data-[dark]/themable:text-black-tint-70 " +
    "group-data-[dark]/themable:hover:bg-black-tint-20 group-data-[dark]/themable:hover:text-black-tint-90 " +
    "group-data-[dark]/themable:disabled:bg-black-tint-15 group-data-[dark]/themable:disabled:text-black-tint-40 " +
    "group-data-[dark]/themable:disabled:hover:bg-black-tint-15 group-data-[dark]/themable:disabled:hover:text-black-tint-40 " +
    "group-data-[dark]/themable:disabled:cursor-not-allowed",

  // Same as kind="gray" here, but no interactivity and doesn't look different when disabled
  grayInert: "bg-white text-black-tint-20 cursor-default hover:text-black-tint-20",

  // TODO: Temporary while we align the help center button with the rest of the buttons
  helpCenter:
    "border-transparent bg-transparent disabled:bg-transparent group-data-[dark]/themable:border-transparent " +
    "group-data-[dark]/themable:bg-transparent hover:group-data-[dark]/themable:bg-transparent",

  // TODO: Temporary while we align the square button styles with the rest of the buttons
  // This seems to be closest to "gray", but we need to scan for differences and make sure using "gray" instead
  // would work for all use cases.
  squareLegacy:
    "border border-black-tint-97 bg-black-tint-97 text-black-tint-20 hover:text-blue-shade-40 " +
    "disabled:cursor-not-allowed disabled:bg-black-tint-93 disabled:text-black-tint-70 " +
    "data-[selected]:border-blue-tint-40 data-[selected]:bg-blue-tint-93 data-[selected]:text-blue-tint-40 " +
    "group-data-[dark]/themable:border-black-tint-20 group-data-[dark]/themable:bg-black-tint-20 " +
    "group-data-[dark]/themable:text-white hover:group-data-[dark]/themable:bg-black-tint-30 " +
    "disabled:group-data-[dark]/themable:bg-black-tint-20 disabled:group-data-[dark]/themable:text-black-tint-40 " +
    "hover:disabled:group-data-[dark]/themable:bg-black-tint-20 " +
    "group-data-[dark]/themable:data-[selected]:border-blue-shade-20 " +
    "group-data-[dark]/themable:data-[selected]:bg-blue-shade-40",

  // TODO: This is just used for the audio video options menu so far, it only needs to have no border or background
  transparentSquareLegacy:
    "text-black-tint-20 hover:text-blue-shade-40 " +
    "hover:bg-black-tint-97 " +
    "disabled:cursor-not-allowed disabled:bg-black-tint-93 disabled:text-black-tint-70 " +
    "data-[selected]:border-blue-tint-40 data-[selected]:bg-blue-tint-93 data-[selected]:text-blue-tint-40 " +
    "group-data-[dark]/themable:border-black-tint-20 group-data-[dark]/themable:bg-transparent " +
    "group-data-[dark]/themable:text-white hover:group-data-[dark]/themable:bg-black-tint-30 " +
    "disabled:group-data-[dark]/themable:bg-black-tint-20 disabled:group-data-[dark]/themable:text-black-tint-40 " +
    "hover:disabled:group-data-[dark]/themable:bg-black-tint-20 " +
    "group-data-[dark]/themable:data-[selected]:border-blue-shade-20 " +
    "group-data-[dark]/themable:data-[selected]:bg-blue-shade-40",

  feature:
    "border border-green bg-white text-green z-[1] " +
    "hover:bg-green-tint-90 hover:text-green-shade-40 " +
    "active:bg-white disabled:bg-black-tint-93 disabled:text-black-tint-70 " +
    "disabled:cursor-not-allowed",

  chipBlue:
    "rounded-3xl border !px-3 py-1 text-blue bg-blue-tint-95 border-blue-tint-70 " +
    "hover:[background:linear-gradient(0deg,rgba(255,255,255,0.40)_0%,rgba(255,255,255,0.40)_100%),var(--blue-tint-95)] " +
    "disabled:cursor-not-allowed disabled:bg-black-tint-97 disabled:border-black-tint-90 disabled:text-black-tint-55",

  chipHibiscus:
    "rounded-3xl border !px-3 py-1 text-hibiscus bg-hibiscus-tint-95 border-hibiscus-tint-70 " +
    "hover:[background:linear-gradient(0deg,rgba(255,255,255,0.40)_0%,rgba(255,255,255,0.40)_100%),var(--hibiscus-tint-95)] " +
    "disabled:cursor-not-allowed disabled:bg-black-tint-97 disabled:border-black-tint-90 disabled:text-black-tint-55",

  chipMoss:
    "rounded-3xl border !px-3 py-1 text-moss bg-moss-tint-95 border-moss-tint-70 " +
    "hover:[background:linear-gradient(0deg,rgba(255,255,255,0.40)_0%,rgba(255,255,255,0.40)_100%),var(--moss-tint-95)] " +
    "disabled:cursor-not-allowed disabled:bg-black-tint-97 disabled:border-black-tint-90 disabled:text-black-tint-55",

  chipOrange:
    "rounded-3xl border !px-3 py-1 text-orange-shade-40 bg-orange-tint-90 border-orange-tint-70 " +
    "hover:[background:linear-gradient(0deg,rgba(255,255,255,0.40)_0%,rgba(255,255,255,0.40)_100%),var(--orange-tint-90)] " +
    "disabled:cursor-not-allowed disabled:bg-black-tint-97 disabled:border-black-tint-90 disabled:text-black-tint-55",

  chipWhite:
    "rounded-3xl border !px-3 py-1 text-black-tint-20 bg-black-tint-99 border-black-tint-90 " +
    "hover:bg-black-tint-97 " +
    "disabled:cursor-not-allowed disabled:bg-black-tint-97 disabled:border-black-tint-90 disabled:text-black-tint-55",
};

const SIZE_CLASSES: Record<IconSizeType, string> = {
  xs: "h-[28px] min-w-[28px] font-medium text-sm p-2",
  s: "h-[32px] min-w-[32px] font-medium text-sm p-2",
  m: "h-[36px] min-w-[36px] font-medium text-sm p-2",
  l: "h-[48px] min-w-[48px] font-medium text-sm p-2",
};

const SIZE_CLASSES_SQUARE: Record<IconSizeType, string> = {
  xs: "size-[28px] min-w-[28px] min-h-[28px] *:size-[14px]",
  s: "size-[32px] min-w-[32px] min-h-[32px] *:size-[16px]",
  m: "size-[36px] min-w-[36px] min-h-[36px] *:size-[16px]",
  l: "size-[44px] min-w-[44px] min-h-[44px] *:size-[18px]",
};

export type ButtonProps<T extends React.ElementType> = BaseButtonProps<T> & {
  isSelected?: boolean;
  kind?: ButtonKind;
  size?: keyof typeof SIZE_CLASSES;
  isSquare?: boolean;
};

/**
 * `Button` is a button that is a rectangle, with rounded corners.
 * It is a polymorphic component, meaning it can be rendered as any HTML element.
 * By default, it is rendered as a <button> element, but you can change it to any other element by passing the `as` prop.
 *
 * To render an `Button` as a link, combine it with the `Link` component from react-router-dom, like this:
 * ```tsx
 * <Button as={Link} to="/somewhere">
 *   This looks like a button, but it's a link!
 * </Button>
 * ```
 */
export const Button = forwardRef(function Button<T extends React.ElementType = "button">(
  { isSelected, kind, size, className, isSquare, disabled, ...rest }: ButtonProps<T>,
  ref: PolymorphicRef<T>,
) {
  return (
    // @ts-expect-error TypeScript doesn't understand that `BaseButton` is polymorphic
    <BaseButton
      className={twMerge(
        "flex w-fit cursor-pointer items-center justify-center whitespace-nowrap rounded-lg",
        kind ? KIND_CLASSES[kind] : "",
        size ? (isSquare ? SIZE_CLASSES_SQUARE[size] : SIZE_CLASSES[size]) : "",
        className,
      )}
      data-selected={isSelected || undefined}
      disabled={disabled}
      ref={ref}
      {...rest}
    />
  );
}) as <T extends React.ElementType = "button">(
  props: ButtonProps<T> & { ref?: PolymorphicRef<T> },
) => React.ReactNode;
